/**
 * AbilityInputMapper — centralizes ability keybind processing.
 *
 * The mapper is now source-agnostic: it consumes the InputSource interface
 * rather than owning keyboard listeners itself.
 */
import { AbilityHolder } from "./AbilitySystem";
import { InputSource } from "./InputSource";

/** How a keybind triggers its ability. */
type ActivationMode =
  | "toggle"
  | "hold"
  | "activate";

export interface AbilityBinding {
  /** The logical button identifier from the active InputSource. */
  key: string;
  /** The ability ID registered in the AbilityHolder (e.g. "auraSight"). */
  abilityId: string;
  /** Which character owns this ability. */
  character: "minna" | "basil";
  /** How pressing the key triggers the ability. */
  mode: ActivationMode;
}

/**
 * Default ability keybindings used by every level unless overridden via
 * `LevelConfig.abilityBindings`. Exported so the canon-catalog contract
 * test (`tests/unit-vitest/ability-catalog.spec.ts`) can enforce that
 * every bound `abilityId` belongs to the canonical 8-ability set.
 */
export const DEFAULT_BINDINGS: AbilityBinding[] = [
  { key: "q", abilityId: "auraSight", character: "minna", mode: "toggle" },
  { key: "e", abilityId: "shadowSlip", character: "minna", mode: "toggle" },
  { key: "f", abilityId: "calmingResonance", character: "minna", mode: "hold" },
  { key: "3", abilityId: "pneumaticAnchor", character: "basil", mode: "toggle" },
  { key: "4", abilityId: "overclockedLeap", character: "basil", mode: "activate" },
  // Key "5" is intentionally unbound. It was previously assigned to
  // `groundingShockwaves`, a code-invented ability retired during the
  // 2026-04-14 canon reconciliation (see `refactor/retire-grounding-shockwaves`).
  // Canon p.25 names "Kitty Grounding shockwaves" as a synonym for the
  // existing Resonant Drop co-op ability, so a second Basil shockwave is
  // redundant. See `tests/unit-vitest/ability-catalog.spec.ts` for the
  // contract that prevents this slot from being repopulated by accident.
];

export class AbilityInputMapper {
  private bindings: AbilityBinding[];

  constructor(bindings: AbilityBinding[] = DEFAULT_BINDINGS) {
    this.bindings = bindings;
  }

  processInput(
    input: InputSource,
    minnaAbilities: AbilityHolder,
    basilAbilities: AbilityHolder,
    onAbilityKey?: (key: string, pressed: boolean, released: boolean) => boolean,
  ): void {
    for (const binding of this.bindings) {
      const holder = binding.character === "minna" ? minnaAbilities : basilAbilities;

      switch (binding.mode) {
        case "toggle":
          if (input.justPressed(binding.key)) {
            const handled = onAbilityKey?.(binding.key, true, false) ?? false;
            if (!handled) {
              const ability = holder.getAbility(binding.abilityId);
              if (ability?.isActive) {
                holder.deactivateAbility(binding.abilityId);
              } else {
                holder.activateAbility(binding.abilityId);
              }
            }
          }
          break;
        case "hold":
          if (input.justPressed(binding.key)) {
            const handled = onAbilityKey?.(binding.key, true, false) ?? false;
            if (!handled) {
              holder.activateAbility(binding.abilityId);
            }
          }
          if (input.justReleased(binding.key)) {
            const handled = onAbilityKey?.(binding.key, false, true) ?? false;
            if (!handled) {
              holder.deactivateAbility(binding.abilityId);
            }
          }
          break;
        case "activate":
          if (input.justPressed(binding.key)) {
            const handled = onAbilityKey?.(binding.key, true, false) ?? false;
            if (!handled) {
              holder.activateAbility(binding.abilityId);
            }
          }
          break;
      }
    }

    const levelSpecificKeys = ["1", "2", ",", "b"];
    for (const key of levelSpecificKeys) {
      if (input.justPressed(key)) {
        onAbilityKey?.(key, true, false);
      }
    }

    // Biscuits paw keys — forward A/D to scenes that host an active
    // ElderBiscuitAnchor so the minigame can read alternating paw
    // presses. We DON'T consume them here because A/D still double as
    // WASD movement when the biscuit session is idle. Scenes that own
    // an active session can return `true` from `onAbilityKey` to
    // suppress movement for that frame; otherwise movement wins.
    for (const pawKey of ["a", "d"]) {
      if (input.justPressed(pawKey)) {
        onAbilityKey?.(pawKey, true, false);
      }
    }

    if ((input.justPressed("f") || input.justPressed("m")) && input.isPressed("f") && input.isPressed("m")) {
      onAbilityKey?.("f+m", true, false);
    }

    if ((input.justPressed("g") || input.justPressed("n")) && input.isPressed("g") && input.isPressed("n")) {
      onAbilityKey?.("g+n", true, false);
    }
  }

  endFrame(): void {
    // InputSource implementations manage edge detection internally.
  }

  dispose(): void {
    // InputSource ownership lives with InputManager.
  }
}
